'use client';

import React from 'react';

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  type DrawerSide,
} from '../../primitives/drawer';

import type { ReactNode } from 'react';

export interface UzDrawerProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** Which edge the drawer slides in from. Defaults to 'right' (side panel). */
  side?: DrawerSide;
  /**
   * Tailwind classes merged onto the DrawerContent. Use this to widen
   * a right/left side panel beyond the 480px default for layouts that
   * need more room (e.g. side-by-side editor + preview). Avoid
   * overriding the panel's `overflow` behaviour from here - children
   * should manage their own scrolling instead.
   */
  contentClassName?: string;
}

/**
 * Slide-in drawer (vaul). Use for side panels like lead detail, filter
 * drawers, edit forms, or any "secondary surface" that shouldn't break the
 * user out of the current page. Defaults to a right-anchored side panel;
 * pass `side="bottom"` for a bottom sheet.
 */
export const UzDrawer = ({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  side = 'right',
  contentClassName,
}: UzDrawerProps): React.JSX.Element => (
  <Drawer open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} direction={side}>
    {trigger && <DrawerTrigger asChild>{trigger}</DrawerTrigger>}
    <DrawerContent
      side={side}
      // Side panels scroll vertically only - clamp x so a wide field/label can
      // never make the drawer scroll sideways.
      //
      // `touch-pan-y!` is essential: vaul tags the content with
      // `[data-vaul-drawer]{touch-action:none}` so it can own the drag gesture,
      // which also kills two-finger / touchscreen scrolling of the body. For a
      // right/left panel the dismiss drag is horizontal, so we hand vertical
      // panning back to the browser (`pan-y`) - the body scrolls again while
      // horizontal swipe-to-dismiss still works. `!` beats vaul's runtime-
      // injected rule regardless of stylesheet order. Bottom sheets keep
      // `touch-action: none` (their drag IS vertical), so this is side-only.
      className={
        [
          side === 'right' || side === 'left'
            ? 'touch-pan-y! overflow-x-hidden overflow-y-auto'
            : undefined,
          contentClassName,
        ]
          .filter(Boolean)
          .join(' ') || undefined
      }
    >
      {(title || description) && (
        <DrawerHeader className={side === 'right' || side === 'left' ? 'text-left' : undefined}>
          {title && <DrawerTitle>{title}</DrawerTitle>}
          {description && <DrawerDescription>{description}</DrawerDescription>}
        </DrawerHeader>
      )}
      <div className="min-w-0 px-4 pb-4">{children}</div>
      {footer && <DrawerFooter>{footer}</DrawerFooter>}
    </DrawerContent>
  </Drawer>
);
